import React, { useEffect, useState } from "react";
import { X, Search, Loader2 } from "lucide-react";
import { getServiceCategories } from "../../../services/services-module-api";
import { useNavigate } from "react-router-dom";

const ServiceCategoryModal = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getServiceCategories();
      if (response.success && response.data && response.data.data) {
        setCategories(response.data.data);
      } else {
        setError("Failed to load service categories");
      }
    } catch (err) {
      setError("An error occurred while loading categories");
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (category) => {
    navigate(`/services/${category._id}`);
    onClose();
  };

  const filteredCategories = categories.filter((category) =>
    category.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col z-10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Select Service Category
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Choose a service category to continue
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#EFA938] focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#EFA938] mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading categories...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchCategories}
                className="px-4 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-600 dark:text-gray-400 mb-2">No categories found</p>
              {searchQuery && (
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Try a different search term
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredCategories.map((category) => (
                <button
                  key={category._id}
                  onClick={() => handleCategorySelect(category)}
                  className="group flex flex-col items-center p-4 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-700 hover:border-[#EFA938] hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all duration-200 hover:shadow-md"
                >
                  <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-[#e1f5fe] dark:bg-blue-900/30 group-hover:bg-[#c8e4fa] dark:group-hover:bg-blue-800/40 transition-colors">
                    {category.icon ? (
                      <img
                        src={category.icon}
                        alt={category.name}
                        className="h-8 w-8 object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="h-8 w-8 rounded-full bg-[#EFA938] flex items-center justify-center text-white font-bold text-sm"
                      style={{ display: category.icon ? "none" : "flex" }}
                    >
                      {category.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white text-center line-clamp-2">
                    {category.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceCategoryModal;

